import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { DollarSign, Eye, Plus, UserMinus, UserPlus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { BulkAssignModal } from '../components/bulk-assign-modal'
import { BulkPriceModal } from '../components/bulk-price-modal'
import { buildProductColumns } from '../components/product-columns'
import type { Product } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable } from '@/components/data-table/data-table'
import { productsApi } from '@/api/endpoints/products'
import { UnassignedProductsHint } from '@/features/categories/components/unassigned-hint'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'

export function ProductsListPage() {
  const navigate = useNavigate()
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false)
  const [bulkPriceModalOpen, setBulkPriceModalOpen] = useState(false)
  const [bulkMode, setBulkMode] = useState<'assign' | 'unassign'>('assign')
  const [selectedSkus, setSelectedSkus] = useState<Array<string>>([])
  const { data: restaurantsData } = useRestaurants()

  const handleCreateProduct = () => {
    navigate({ to: '/products/new' })
  }

  async function fetchProducts({
    pageIndex,
    pageSize,
    filters,
    q,
  }: {
    pageIndex: number
    pageSize: number
    filters?: Record<string, unknown>
    q?: string
  }) {
    const status =
      (filters?.status as 'all' | 'active' | 'inactive' | undefined) ?? 'active'
    const res = await productsApi.list({
      search: q,
      status,
      page: pageIndex,
      pageSize,
    })
    return {
      rows: res.items,
      total: res.total,
      pageCount: Math.ceil(res.total / pageSize),
    }
  }

  const canCreate = can(ACTIONS.PRODUCT_CREATE)
  const canAssign = can(ACTIONS.ASSIGNMENT_MANAGE)
  const canPriceEdit = can(ACTIONS.PRICE_EDIT)

  return (
    <PermissionGate action={ACTIONS.PRODUCT_VIEW}>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">{tProducts('list.title')}</h1>
          {canCreate && (
            <Button onClick={handleCreateProduct}>
              <Plus className="w-4 h-4 mr-2" />
              {tProducts('list.create')}
            </Button>
          )}
        </div>

        <UnassignedProductsHint />

        <Card>
          <CardContent className="pt-6">
            <DataTable<Product>
              columns={buildProductColumns(tCommon, tProducts)}
              fetcher={fetchProducts}
              filters={[
                {
                  id: 'status',
                  columnId: 'active',
                  label: tCommon('status'),
                  type: 'select',
                  options: [
                    { label: tCommon('all'), value: 'all' },
                    { label: tCommon('active'), value: 'active' },
                    { label: tCommon('inactive'), value: 'inactive' },
                  ],
                },
              ]}
              globalSearch={{
                placeholder: tProducts('list.searchPlaceholder'),
              }}
              selectable
              enableColumnVisibility
              showDensityToggle
              initialState={{ pageSize: 20 }}
              getRowId={(row) => row.sku}
              rowActions={(row) => (
                <Button variant="ghost" size="sm" asChild>
                  <Link
                    to={'/products/$sku'}
                    params={{ sku: row.original.sku }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {tCommon('view')}
                  </Link>
                </Button>
              )}
              bulkActions={(rows) => {
                const skus = rows.map((r) => r.original.sku)
                if (!canAssign && !canPriceEdit) return null
                return (
                  <div className="flex gap-2">
                    {canAssign && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedSkus(skus)
                            setBulkMode('assign')
                            setBulkAssignModalOpen(true)
                          }}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />{' '}
                          {tProducts('list.bulkAssign')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedSkus(skus)
                            setBulkMode('unassign')
                            setBulkAssignModalOpen(true)
                          }}
                        >
                          <UserMinus className="w-4 h-4 mr-2" />{' '}
                          {tProducts('list.bulkUnassign')}
                        </Button>
                      </>
                    )}
                    {canPriceEdit && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedSkus(skus)
                          setBulkPriceModalOpen(true)
                        }}
                      >
                        <DollarSign className="w-4 h-4 mr-2" />{' '}
                        {tProducts('list.bulkUpdatePrices')}
                      </Button>
                    )}
                  </div>
                )
              }}
            />
          </CardContent>
        </Card>

        <BulkAssignModal
          open={bulkAssignModalOpen}
          onClose={() => setBulkAssignModalOpen(false)}
          selectedSkus={selectedSkus}
          restaurants={restaurantsData?.items || []}
          mode={bulkMode}
        />

        <BulkPriceModal
          open={bulkPriceModalOpen}
          onClose={() => setBulkPriceModalOpen(false)}
          selectedSkus={selectedSkus}
          restaurants={restaurantsData?.items || []}
        />
      </div>
    </PermissionGate>
  )
}
